import React from 'react';
import { SecurityStatus, SeverityLevel } from '../../types';

export const StatusIndicator: React.FC<{ status: SecurityStatus; size?: 'sm' | 'md' | 'lg'; showLabel?: boolean }> = ({
  status,
  size = 'md',
  showLabel = false
}) => {
  const config = {
    good: {
      color: 'bg-emerald-500',
      text: 'text-emerald-400',
      bg: 'bg-emerald-950/60 border-emerald-800/60',
      label: 'Good'
    },
    attention: {
      color: 'bg-amber-500',
      text: 'text-amber-400',
      bg: 'bg-amber-950/60 border-amber-800/60',
      label: 'Needs attention'
    },
    risk: {
      color: 'bg-rose-500',
      text: 'text-rose-400',
      bg: 'bg-rose-950/60 border-rose-800/60',
      label: 'High risk'
    }
  }[status] || {
    color: 'bg-slate-500',
    text: 'text-slate-400',
    bg: 'bg-slate-900 border-slate-700',
    label: 'Unknown'
  };

  const dotSize = {
    sm: 'w-2 h-2',
    md: 'w-2.5 h-2.5',
    lg: 'w-3.5 h-3.5'
  }[size];

  if (!showLabel) {
    return <span className={`inline-block rounded-full ${dotSize} ${config.color} shadow-sm`} />;
  }

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${config.bg} ${config.text}`}>
      <span className={`rounded-full ${dotSize} ${config.color}`} />
      {config.label}
    </span>
  );
};

export const SeverityBadge: React.FC<{ severity: SeverityLevel }> = ({ severity }) => {
  const config = {
    LOW: {
      bg: 'bg-slate-800 border-slate-700 text-slate-300',
      label: 'LOW • Informational'
    },
    MEDIUM: {
      bg: 'bg-amber-950/70 border-amber-800/60 text-amber-300',
      label: 'MEDIUM • Attention'
    },
    HIGH: {
      bg: 'bg-orange-950/80 border-orange-700/70 text-orange-300',
      label: 'HIGH • Potential Fraud'
    },
    CRITICAL: {
      bg: 'bg-rose-950/90 border-rose-700/80 text-rose-300 animate-pulse-subtle',
      label: 'CRITICAL • Active ID Threat'
    }
  }[severity];

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${config.bg}`}>
      {config.label}
    </span>
  );
};
